/**
 * GiftCardPurchasePage.jsx
 *
 * Public-facing buyer flow at /gift-card/:orgSlug.
 *
 * Phase 4 features:
 * - Buyer selects amount (suggested chip OR custom typed amount)
 * - Fills recipient + buyer details
 * - Optional personal message (500 chars)
 * - Optional SMS delivery (requires recipient phone)
 * - Optional schedule date (max 1 year)
 * - Stripe Checkout redirect for payment
 *
 * Brand colors come from the operator's primary_color setting; falls back to
 * BookingFlows blue. Layout is a single-column, mobile-first form.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Gift, ChevronLeft, Calendar, MessageSquare, Send, Loader2,
  ShieldCheck, Sparkles, CreditCard,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const fmtMoney = (amount, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
      currencyDisplay: "narrowSymbol",
    }).format(Number(amount || 0));
  } catch {
    return `${Number(amount || 0).toFixed(2)} ${currency}`;
  }
};

const minDate = () => new Date().toISOString().slice(0, 10);
const maxDate = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

export default function GiftCardPurchasePage() {
  const { orgSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [usingCustom, setUsingCustom] = useState(false);
  const [purchaserName, setPurchaserName] = useState("");
  const [purchaserEmail, setPurchaserEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [sendSms, setSendSms] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");

  useEffect(() => {
    if (searchParams.get("cancelled")) {
      toast.info("Checkout cancelled — your card has not been charged.");
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/v2/public/gift-card-purchases/config/${orgSlug}`);
        if (cancelled) return;
        setConfig(res.data);
        if (res.data?.suggested_amounts?.length) {
          setAmount(res.data.suggested_amounts[1] || res.data.suggested_amounts[0]);
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail === "operator_not_found" ? "Operator not found" : "Could not load gift card page");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgSlug]);

  const effectiveAmount = useMemo(() => {
    if (usingCustom) {
      const v = parseFloat(customAmount);
      return Number.isFinite(v) ? v : 0;
    }
    return Number(amount) || 0;
  }, [usingCustom, customAmount, amount]);

  const primary = config?.primary_color || "#2563EB";
  const currency = config?.currency || "USD";

  const validationError = useMemo(() => {
    if (!config) return null;
    if (effectiveAmount < config.min_amount) return `Minimum amount is ${fmtMoney(config.min_amount, currency)}`;
    if (effectiveAmount > config.max_amount) return `Maximum amount is ${fmtMoney(config.max_amount, currency)}`;
    if (!purchaserName.trim()) return "Your name is required";
    if (!purchaserEmail.trim()) return "Your email is required";
    if (!recipientName.trim()) return "Recipient name is required";
    if (!recipientEmail.trim()) return "Recipient email is required";
    if (sendSms && !recipientPhone.trim()) return "Recipient phone is required for SMS delivery";
    if (scheduleEnabled && !scheduleDate) return "Please pick a delivery date";
    return null;
  }, [config, effectiveAmount, currency, purchaserName, purchaserEmail, recipientName, recipientEmail, sendSms, recipientPhone, scheduleEnabled, scheduleDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        org_slug: orgSlug,
        amount: effectiveAmount,
        currency,
        purchaser_name: purchaserName.trim(),
        purchaser_email: purchaserEmail.trim(),
        recipient_name: recipientName.trim(),
        recipient_email: recipientEmail.trim(),
        recipient_phone: recipientPhone.trim() || undefined,
        personal_message: personalMessage.trim() || undefined,
        send_sms: sendSms,
        scheduled_delivery_date: scheduleEnabled ? scheduleDate : undefined,
      };
      const res = await axios.post(`${API_URL}/api/v2/public/gift-card-purchases`, payload);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Could not start checkout");
        setSubmitting(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not start checkout";
      toast.error(typeof detail === "string" ? detail : "Could not start checkout");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="text-xl font-semibold text-gray-900 mb-2">Operator not found</div>
        <Link to="/" className="text-blue-600 hover:underline">Back to marketplace</Link>
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md text-center bg-white rounded-2xl shadow-md p-8 border border-gray-100">
          <Gift className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <div className="text-xl font-semibold text-gray-900 mb-2">Gift cards are not available yet</div>
          <p className="text-gray-600 mb-6">{config.org_name} hasn’t enabled public gift card purchases. Reach out to them directly to see if they can issue one for you.</p>
          {config.operator_website && (
            <a href={config.operator_website.startsWith("http") ? config.operator_website : `https://${config.operator_website}`}
               target="_blank" rel="noreferrer"
               className="inline-block px-5 py-2 rounded-full bg-gray-900 text-white hover:bg-gray-700 text-sm">
              Visit operator website
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="gift-card-purchase-page">
      {/* Nav */}
      <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 pt-[env(safe-area-inset-top)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="gift-card-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <Link to={`/host/${orgSlug}`} className="text-sm font-medium text-gray-700 hover:text-gray-900">
            View operator profile
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-6 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl shadow-sm mb-4"
             style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)` }}>
          <Gift className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Give a gift card</h1>
        <p className="text-gray-600 max-w-xl mx-auto">
          The perfect gift for {config.org_name}. Delivered by email, redeemable anytime within {config.default_expiry_months} months.
        </p>
      </header>

      {/* Form */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pb-24">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Amount selection */}
          <section className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 sm:p-7">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Choose an amount</h2>
            <p className="text-sm text-gray-500 mb-4">{fmtMoney(config.min_amount, currency)} – {fmtMoney(config.max_amount, currency)}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {config.suggested_amounts.map((a) => {
                const active = !usingCustom && Number(amount) === Number(a);
                return (
                  <button
                    type="button"
                    key={a}
                    onClick={() => { setUsingCustom(false); setAmount(a); }}
                    className={`py-3 rounded-xl text-center font-semibold transition-all border ${active ? "text-white" : "bg-gray-50 text-gray-900 border-transparent hover:border-gray-300"}`}
                    style={active ? { background: primary, borderColor: primary } : {}}
                    data-testid={`gift-card-amount-${a}`}
                  >
                    {fmtMoney(a, currency)}
                  </button>
                );
              })}
            </div>
            <div className="mt-4">
              <label className="block text-sm text-gray-600 mb-1.5">Or enter a custom amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">{currency}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="1"
                  min={config.min_amount}
                  max={config.max_amount}
                  value={customAmount}
                  onChange={(e) => { setUsingCustom(true); setCustomAmount(e.target.value); }}
                  placeholder="Custom amount"
                  className="w-full pl-14 pr-3 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 transition-colors"
                  data-testid="gift-card-custom-amount"
                />
              </div>
            </div>
          </section>

          {/* Recipient */}
          <section className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 sm:p-7">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Who's it for?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Recipient's name" required value={recipientName} onChange={setRecipientName} placeholder="Alex Morgan" testId="gift-card-recipient-name" />
              <Field label="Recipient's email" required type="email" value={recipientEmail} onChange={setRecipientEmail} placeholder="alex@example.com" testId="gift-card-recipient-email" />
            </div>
            <label className="mt-4 flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} className="w-4 h-4 rounded text-blue-600" data-testid="gift-card-send-sms-toggle" />
              <span className="text-sm text-gray-700">Also send by {currency === "AED" ? "WhatsApp" : "SMS"} (free, requires phone number)</span>
            </label>
            {sendSms && (
              <div className="mt-3">
                <Field label="Recipient phone" required value={recipientPhone} onChange={setRecipientPhone} placeholder={currency === "AED" ? "+971 50 123 4567" : "+1 555 010 0123"} testId="gift-card-recipient-phone" />
              </div>
            )}

            <div className="mt-5">
              <label className="block text-sm text-gray-600 mb-1.5 flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5" />
                Personal message (optional, {personalMessage.length}/500)
              </label>
              <textarea
                value={personalMessage}
                onChange={(e) => setPersonalMessage(e.target.value.slice(0, 500))}
                rows={3}
                placeholder={`Happy birthday! Can't wait to plan something with ${config.org_name}...`}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                data-testid="gift-card-personal-message"
              />
            </div>
          </section>

          {/* Delivery scheduling */}
          <section className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 sm:p-7">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> When should we deliver?
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setScheduleEnabled(false)}
                className={`py-3 rounded-xl text-sm font-semibold border transition-all ${!scheduleEnabled ? "text-white" : "bg-gray-50 text-gray-900 border-transparent hover:border-gray-300"}`}
                style={!scheduleEnabled ? { background: primary, borderColor: primary } : {}}
                data-testid="gift-card-schedule-now"
              >
                Send now
              </button>
              <button
                type="button"
                onClick={() => setScheduleEnabled(true)}
                className={`py-3 rounded-xl text-sm font-semibold border transition-all ${scheduleEnabled ? "text-white" : "bg-gray-50 text-gray-900 border-transparent hover:border-gray-300"}`}
                style={scheduleEnabled ? { background: primary, borderColor: primary } : {}}
                data-testid="gift-card-schedule-later"
              >
                Schedule for later
              </button>
            </div>
            {scheduleEnabled && (
              <div className="mt-4">
                <label className="block text-sm text-gray-600 mb-1.5">Delivery date (within 1 year)</label>
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={minDate()}
                  max={maxDate()}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 transition-colors"
                  data-testid="gift-card-schedule-date"
                />
                <p className="text-xs text-gray-500 mt-1.5">We'll send the email and (optional) SMS that morning in the operator's timezone.</p>
              </div>
            )}
          </section>

          {/* Buyer */}
          <section className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 sm:p-7">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Your details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Your name" required value={purchaserName} onChange={setPurchaserName} placeholder="Jamie Lee" testId="gift-card-purchaser-name" />
              <Field label="Your email" required type="email" value={purchaserEmail} onChange={setPurchaserEmail} placeholder="jamie@example.com" testId="gift-card-purchaser-email" />
            </div>
            <p className="text-xs text-gray-500 mt-3">Your email is used for the receipt only and is never shared with the recipient.</p>
          </section>

          {/* Trust */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500 py-2">
            <div className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secure checkout via Stripe</div>
            <div className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Recipient sees a beautiful card</div>
            <div className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> No fees</div>
          </div>

          {/* CTA */}
          <div className="sticky bottom-0 -mx-4 px-4 sm:mx-0 sm:px-0 pt-4 pb-[env(safe-area-inset-bottom,16px)] bg-gradient-to-t from-white via-white/95 to-transparent">
            <button
              type="submit"
              disabled={submitting || !!validationError}
              className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-semibold text-base shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{ background: primary }}
              data-testid="gift-card-purchase-submit"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to Stripe…</>
              ) : (
                <><Send className="w-5 h-5" /> Pay {fmtMoney(effectiveAmount, currency)} & send gift card</>
              )}
            </button>
            {validationError && (
              <p className="text-center text-xs text-red-600 mt-2" data-testid="gift-card-purchase-error">{validationError}</p>
            )}
          </div>
        </form>
      </main>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-sm text-gray-600 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 transition-colors"
        data-testid={testId}
      />
    </div>
  );
}
